import React, { useState, useEffect } from 'react';
import Lottie from 'react-lottie';
import './LoadingOverlay.css';

interface LoadingOverlayProps {
  visible: boolean;
  messages: string[];
  isSuccess: boolean;
  onFadeOut: () => void;
}

const LoadingOverlay = ({ visible, messages, isSuccess, onFadeOut }: LoadingOverlayProps) => {
  // State for the Lottie animation data
  const [animationData, setAnimationData] = useState<any>();
  
  // State for the messages and fade-out effect
  const [currentMessage, setCurrentMessage] = useState(messages.length > 0 ? messages[0] : '');
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Effect #1: Fetch the Lottie animation from the public folder (runs once)
  useEffect(() => {
    fetch('/loading.json')
      .then(response => response.json())
      .then(data => setAnimationData(data))
      .catch(error => console.error("Error loading Lottie animation:", error));
  }, []);

  // Effect #2: Cycle through the loading messages (runs when visible or messages change)
  useEffect(() => {
    // Only cycle messages if the overlay is visible and not in success state
    if (visible && !isSuccess && messages.length > 0) {
      const interval = setInterval(() => {
        setCurrentMessage(prev => {
          const currentIndex = messages.indexOf(prev);
          const nextIndex = (currentIndex + 1) % messages.length;
          return messages[nextIndex];
        });
      }, 2000); // Change message every 2 seconds

      return () => clearInterval(interval); // Cleanup on unmount or when visibility changes
    }
  }, [visible, isSuccess, messages]);

  // Effect #3: Handle the success state and trigger the fade-out
  useEffect(() => {
    if (isSuccess) {
      setCurrentMessage("Success! Redirecting...");
      setIsFadingOut(true); // Trigger fade-out animation
      
      const timer = setTimeout(() => {
        onFadeOut(); // Call the parent function to hide the component entirely
      }, 1500); // Wait for animation to finish

      return () => clearTimeout(timer);
    }
  }, [isSuccess, onFadeOut]);

  // Don't render anything if not visible or if the animation data hasn't loaded yet
  if (!visible || !animationData) {
    return null;
  }

  // Lottie player options
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <div className={`overlay ${isFadingOut ? 'fade-out' : ''}`}>
      <div className="container">
        <Lottie options={defaultOptions} height={150} width={250} />
        {/* This now uses the state variable for the message */}
        <p className="loading-message">{currentMessage}</p>
      </div>
    </div>
  );
};

export default LoadingOverlay;